import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
    ArrowPathIcon,
    ArrowTopRightOnSquareIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ClockIcon,
    DocumentTextIcon,
    MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { SECTION_PERMISSIONS } from "@/config/permissions";
import { usePermissions } from "@/features/auth/usePermissions";
import { getDrivers } from "@/features/drivers/api/driversApi";
import { extractApiError } from "@/lib/api-error";

import {
    getDriverLicenseVerifications,
    requeueDriverLicenseVerification,
} from "../api/driverLicenceApi";
import type {
    DriverLicenseVerificationItem,
    DriverLicenseVerificationQueryParams,
    LicenseVerificationStatus,
} from "../types";

const STATUS_OPTIONS: Array<{
    value: LicenseVerificationStatus;
    label: string;
    tone: string;
}> = [
    {
        value: "SUBMITTED",
        label: "Submitted",
        tone: "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300",
    },
    {
        value: "OCR_PROCESSING",
        label: "OCR Processing",
        tone: "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900/50 dark:bg-sky-950/30 dark:text-sky-300",
    },
    {
        value: "OCR_FAILED",
        label: "OCR Failed",
        tone: "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300",
    },
    {
        value: "CROSS_CHECK_FAILED",
        label: "Cross-check Failed",
        tone: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300",
    },
    {
        value: "PROVISIONALLY_APPROVED",
        label: "Provisional",
        tone: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-300",
    },
    {
        value: "PENDING_BULK_VALIDATION",
        label: "Pending Provider",
        tone: "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900/50 dark:bg-violet-950/30 dark:text-violet-300",
    },
    {
        value: "FULLY_VERIFIED",
        label: "Fully Verified",
        tone: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-300",
    },
    {
        value: "SUSPENDED",
        label: "Suspended",
        tone: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300",
    },
];

const STATUS_META = Object.fromEntries(
    STATUS_OPTIONS.map((option) => [option.value, option])
) as Record<LicenseVerificationStatus, (typeof STATUS_OPTIONS)[number]>;

const formatDateTime = (value: string | null | undefined) => {
    if (!value) return "—";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat("en-NG", {
        dateStyle: "medium",
        timeStyle: "short",
    }).format(date);
};

const formatRelativeTime = (value: string | null | undefined) => {
    if (!value) return "—";

    const timestamp = new Date(value).getTime();
    if (Number.isNaN(timestamp)) return value;

    const diffMs = timestamp - Date.now();
    const absoluteMinutes = Math.round(Math.abs(diffMs) / 60_000);

    if (absoluteMinutes < 1) return "Now";
    if (absoluteMinutes < 60) {
        const unit = absoluteMinutes === 1 ? "minute" : "minutes";
        return diffMs >= 0
            ? `In ${absoluteMinutes} ${unit}`
            : `${absoluteMinutes} ${unit} ago`;
    }

    const absoluteHours = Math.round(absoluteMinutes / 60);
    if (absoluteHours < 24) {
        const unit = absoluteHours === 1 ? "hour" : "hours";
        return diffMs >= 0
            ? `In ${absoluteHours} ${unit}`
            : `${absoluteHours} ${unit} ago`;
    }

    const absoluteDays = Math.round(absoluteHours / 24);
    const unit = absoluteDays === 1 ? "day" : "days";
    return diffMs >= 0 ? `In ${absoluteDays} ${unit}` : `${absoluteDays} ${unit} ago`;
};

const buildDriverName = (item: DriverLicenseVerificationItem) => {
    const fullName = [item.firstName, item.lastName].filter(Boolean).join(" ").trim();
    return fullName || "Unknown driver";
};

const getVerificationSortValue = (value: string | null | undefined) => {
    if (!value) return Number.NEGATIVE_INFINITY;

    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
};

const compareVerificationRecency = (
    left: DriverLicenseVerificationItem,
    right: DriverLicenseVerificationItem
) => {
    const updatedAtDiff =
        getVerificationSortValue(right.updatedAt) - getVerificationSortValue(left.updatedAt);
    if (updatedAtDiff !== 0) return updatedAtDiff;

    const attemptDiff = right.attemptNumber - left.attemptNumber;
    if (attemptDiff !== 0) return attemptDiff;

    return (
        getVerificationSortValue(right.submittedAt) -
        getVerificationSortValue(left.submittedAt)
    );
};

const selectLatestVerificationPerDriver = (
    items: DriverLicenseVerificationItem[]
) => {
    const latestByDriver = new Map<string, DriverLicenseVerificationItem>();

    items.forEach((item) => {
        const current = latestByDriver.get(item.driverId);
        if (!current || compareVerificationRecency(item, current) < 0) {
            latestByDriver.set(item.driverId, item);
        }
    });

    return Array.from(latestByDriver.values()).sort(compareVerificationRecency);
};

export const DriverLicenceRoute = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { hasPermission } = usePermissions();
    const canManageApprovals = hasPermission(SECTION_PERMISSIONS.APPROVALS_MANAGE[0]);

    const [filters, setFilters] = useState<{
        page: number;
        size: number;
        statuses: LicenseVerificationStatus[];
        driverLookup: string;
    }>({
        page: 0,
        size: 20,
        statuses: [],
        driverLookup: "",
    });
    const [feedback, setFeedback] = useState<{
        tone: "success" | "error";
        text: string;
    } | null>(null);
    const [requeueingId, setRequeueingId] = useState<string | null>(null);

    useEffect(() => {
        if (!feedback) return undefined;

        const timeoutId = window.setTimeout(() => setFeedback(null), 5000);
        return () => window.clearTimeout(timeoutId);
    }, [feedback]);

    const normalizedDriverLookup = filters.driverLookup.trim().toLowerCase();

    const driverLookupQuery = useQuery({
        queryKey: ["driver-license-driver-lookup", normalizedDriverLookup],
        queryFn: () =>
            getDrivers({
                search: filters.driverLookup.trim(),
                page: 0,
                size: 20,
            }),
        enabled: Boolean(normalizedDriverLookup),
    });

    const resolvedDriverId = useMemo(() => {
        if (!normalizedDriverLookup) return undefined;

        const drivers = driverLookupQuery.data?.data.drivers ?? [];
        const exactMatch = drivers.find((driver) => {
            const fullName = `${driver.firstName} ${driver.lastName}`.trim().toLowerCase();
            return (
                driver.email.toLowerCase() === normalizedDriverLookup ||
                driver.phoneNumber.toLowerCase() === normalizedDriverLookup ||
                driver.userId.toLowerCase() === normalizedDriverLookup ||
                fullName === normalizedDriverLookup
            );
        });

        return exactMatch?.userId;
    }, [driverLookupQuery.data?.data.drivers, normalizedDriverLookup]);

    const queryParams = useMemo<DriverLicenseVerificationQueryParams>(
        () => ({
            page: filters.page,
            size: filters.size,
            statuses: filters.statuses.length ? filters.statuses : undefined,
            driverId: resolvedDriverId,
        }),
        [filters.page, filters.size, filters.statuses, resolvedDriverId]
    );

    const { data, isLoading, error, isFetching, refetch } = useQuery({
        queryKey: ["driver-license-verifications", queryParams],
        queryFn: async () => {
            if (normalizedDriverLookup && !resolvedDriverId) {
                return {
                    code: 200,
                    status: true,
                    message: "No matching driver",
                    data: {
                        items: [],
                        page: 0,
                        size: filters.size,
                        totalElements: 0,
                        totalPages: 0,
                        hasNext: false,
                        hasPrevious: false,
                    },
                };
            }

            return getDriverLicenseVerifications(queryParams);
        },
        enabled: !normalizedDriverLookup || !driverLookupQuery.isLoading,
    });

    const requeueMutation = useMutation({
        mutationFn: (verificationId: string) =>
            requeueDriverLicenseVerification(verificationId),
        onMutate: (verificationId) => {
            setRequeueingId(verificationId);
            setFeedback(null);
        },
        onSuccess: async (response, verificationId) => {
            await queryClient.invalidateQueries({
                queryKey: ["driver-license-verifications"],
            });
            await queryClient.invalidateQueries({
                queryKey: ["driver-license-verification", verificationId],
            });
            setFeedback({
                tone: "success",
                text: response.message || response.data.message || "Verification requeued.",
            });
        },
        onError: (mutationError) => {
            setFeedback({
                tone: "error",
                text: extractApiError(mutationError),
            });
        },
        onSettled: () => {
            setRequeueingId(null);
        },
    });

    const queue = data?.data;
    const rawItems = queue?.items ?? [];
    const items = useMemo(
        () => selectLatestVerificationPerDriver(rawItems),
        [rawItems]
    );
    const hiddenAttemptCount = rawItems.length - items.length;
    const setFilter = <TKey extends keyof typeof filters>(
        key: TKey,
        value: (typeof filters)[TKey]
    ) => {
        setFilters((previous) => ({
            ...previous,
            [key]: value,
            ...(key === "page" ? {} : { page: 0 }),
        }));
    };

    const toggleStatus = (status: LicenseVerificationStatus) => {
        setFilters((previous) => {
            const exists = previous.statuses.includes(status);
            const nextStatuses = exists
                ? previous.statuses.filter((entry) => entry !== status)
                : [...previous.statuses, status];

            return {
                ...previous,
                page: 0,
                statuses: nextStatuses,
            };
        });
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-24">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700 dark:border-rose-900/60 dark:bg-rose-900/20 dark:text-rose-300">
                {extractApiError(error)}
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-50">
                            Driver licence queue
                        </h2>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                            Review provisional, failed, and provider-stage records.
                        </p>
                    </div>
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                        {queue?.totalElements ?? items.length} records
                    </div>
                </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_140px_auto]">
                    <label className="space-y-2 text-sm">
                        <span className="font-medium text-slate-700 dark:text-slate-200">
                            Driver
                        </span>
                        <div className="relative">
                            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                            <input
                                value={filters.driverLookup}
                                onChange={(event) =>
                                    setFilter("driverLookup", event.target.value)
                                }
                                placeholder="Email or phone"
                                className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-4 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                            />
                        </div>
                    </label>

                    <label className="space-y-2 text-sm">
                        <span className="font-medium text-slate-700 dark:text-slate-200">
                            Rows
                        </span>
                        <select
                            value={filters.size}
                            onChange={(event) => setFilter("size", Number(event.target.value))}
                            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                        >
                            {[10, 20, 50].map((size) => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                    </label>

                    <div className="flex flex-wrap items-end gap-3 lg:justify-end">
                        <Button
                            variant="outline"
                            onClick={() =>
                                setFilters({
                                    page: 0,
                                    size: 20,
                                    statuses: [],
                                    driverLookup: "",
                                })
                            }
                        >
                            Reset
                        </Button>
                        <Button variant="outline" onClick={() => refetch()}>
                            <ArrowPathIcon className="mr-2 h-4 w-4" />
                            Refresh
                        </Button>
                    </div>
                </div>

                <div className="mt-4 flex flex-col gap-2 lg:flex-row lg:items-center">
                    <span className="shrink-0 text-sm font-medium text-slate-700 dark:text-slate-200">
                        Status
                    </span>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={() => setFilter("statuses", [])}
                            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                                filters.statuses.length === 0
                                    ? "border-slate-900 bg-slate-900 text-white dark:border-slate-100 dark:bg-slate-100 dark:text-slate-900"
                                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:border-slate-600 dark:hover:text-slate-100"
                            }`}
                        >
                            All
                        </button>
                        {STATUS_OPTIONS.map((statusOption) => {
                            const active = filters.statuses.includes(statusOption.value);
                            return (
                                <button
                                    key={statusOption.value}
                                    type="button"
                                    onClick={() => toggleStatus(statusOption.value)}
                                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                                        active
                                            ? "border-slate-900 bg-slate-900 text-white dark:border-slate-100 dark:bg-slate-100 dark:text-slate-900"
                                            : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:border-slate-600 dark:hover:text-slate-100"
                                    }`}
                                >
                                    {statusOption.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="mt-2 flex items-center justify-between gap-3 text-xs text-slate-500 dark:text-slate-400">
                    <span>
                        {filters.statuses.length === 0
                            ? "All statuses"
                            : `${filters.statuses.length} selected`}
                    </span>
                    {driverLookupQuery.isLoading ? <span>Finding driver...</span> : null}
                </div>

                {normalizedDriverLookup && !driverLookupQuery.isLoading && resolvedDriverId ? (
                    <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                        Filtering by matched driver.
                    </p>
                ) : null}

                {normalizedDriverLookup && !driverLookupQuery.isLoading && !resolvedDriverId ? (
                    <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">
                        No driver matched that email or phone.
                    </p>
                ) : null}
            </section>

            {feedback ? (
                <div
                    className={`rounded-2xl px-5 py-4 text-sm ${
                        feedback.tone === "success"
                            ? "border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-900/20 dark:text-emerald-300"
                            : "border border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/60 dark:bg-rose-900/20 dark:text-rose-300"
                    }`}
                >
                    {feedback.text}
                </div>
            ) : null}

            <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-800">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                        Queue
                    </h3>
                    {isFetching ? (
                        <div className="inline-flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                            <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                            Refreshing
                        </div>
                    ) : null}
                </div>

                {items.length === 0 ? (
                    <div className="px-6 py-14 text-center">
                        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
                            <DocumentTextIcon className="h-6 w-6 text-slate-400" />
                        </div>
                        <h4 className="mt-4 text-base font-semibold text-slate-900 dark:text-slate-100">
                            No records found
                        </h4>
                        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                            Adjust the filters and try again.
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-800">
                            <thead className="bg-slate-50 dark:bg-slate-950/50">
                                <tr className="text-left text-xs uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                                    <th className="px-6 py-3.5 font-medium">Driver</th>
                                    <th className="px-6 py-3.5 font-medium">Licence</th>
                                    <th className="px-6 py-3.5 font-medium">Status</th>
                                    <th className="px-6 py-3.5 font-medium">Provider</th>
                                    <th className="px-6 py-3.5 font-medium">Attempts</th>
                                    <th className="px-6 py-3.5 font-medium">Next Retry</th>
                                    <th className="px-6 py-3.5 font-medium">Submitted</th>
                                    <th className="px-6 py-3.5 font-medium text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {items.map((item) => {
                                    const statusMeta = STATUS_META[item.status];
                                    const canQuickRequeue =
                                        canManageApprovals &&
                                        item.status === "PENDING_BULK_VALIDATION";

                                    return (
                                        <tr
                                            key={item.verificationId}
                                            className="hover:bg-slate-50/80 dark:hover:bg-slate-950/30"
                                        >
                                            <td className="px-6 py-4 align-top">
                                                <div className="space-y-1">
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            navigate(
                                                                `/approvals/driver-licences/${item.verificationId}`
                                                            )
                                                        }
                                                        className="text-left font-semibold text-slate-900 hover:text-primary-600 dark:text-slate-100 dark:hover:text-primary-400"
                                                    >
                                                        {buildDriverName(item)}
                                                    </button>
                                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                                        {item.email || item.phoneNumber || item.driverId}
                                                    </p>
                                                    <p className="font-mono text-[11px] text-slate-400 dark:text-slate-500">
                                                        {item.driverId}
                                                    </p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 align-top">
                                                <div className="space-y-1">
                                                    <p className="font-medium text-slate-900 dark:text-slate-100">
                                                        {item.submittedLicenseNumber || "—"}
                                                    </p>
                                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                                        Issue {formatDateTime(item.submittedIssueDate)}
                                                    </p>
                                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                                        Expiry {formatDateTime(item.submittedExpiryDate)}
                                                    </p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 align-top">
                                                <div className="space-y-2">
                                                    <span
                                                        className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${statusMeta.tone}`}
                                                    >
                                                        {statusMeta.label}
                                                    </span>
                                                    <div className="space-y-1 text-xs text-slate-500 dark:text-slate-400">
                                                        {item.rejectionReason ? (
                                                            <p>{item.rejectionReason}</p>
                                                        ) : null}
                                                        {item.suspensionReason ? (
                                                            <p>{item.suspensionReason}</p>
                                                        ) : null}
                                                        {item.providerLastError ? (
                                                            <p>{item.providerLastError}</p>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 align-top">
                                                <div className="space-y-1">
                                                    <p className="font-medium text-slate-900 dark:text-slate-100">
                                                        {item.provider || "—"}
                                                    </p>
                                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                                        {item.providerResult || "No result yet"}
                                                    </p>
                                                    {item.documentUrl ? (
                                                        <a
                                                            href={item.documentUrl}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400"
                                                        >
                                                            Document
                                                            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                                        </a>
                                                    ) : null}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 align-top">
                                                <div className="space-y-1 text-xs text-slate-500 dark:text-slate-400">
                                                    <p className="font-medium text-slate-900 dark:text-slate-100">
                                                        Attempt #{item.attemptNumber}
                                                    </p>
                                                    <p>
                                                        Provider tries:{" "}
                                                        {item.providerAttemptCount ?? 0}
                                                    </p>
                                                    <p>
                                                        Last provider touch:{" "}
                                                        {formatRelativeTime(
                                                            item.providerLastAttemptAt
                                                        )}
                                                    </p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 align-top">
                                                <div className="inline-flex items-start gap-2 text-xs text-slate-500 dark:text-slate-400">
                                                    <ClockIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                                    <div>
                                                        <p className="font-medium text-slate-900 dark:text-slate-100">
                                                            {formatRelativeTime(item.nextRetryAt)}
                                                        </p>
                                                        <p>{formatDateTime(item.nextRetryAt)}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 align-top">
                                                <div className="space-y-1 text-xs text-slate-500 dark:text-slate-400">
                                                    <p className="font-medium text-slate-900 dark:text-slate-100">
                                                        {formatDateTime(item.submittedAt)}
                                                    </p>
                                                    <p>Updated {formatRelativeTime(item.updatedAt)}</p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 align-top">
                                                <div className="flex items-center justify-end gap-2">
                                                    {canQuickRequeue ? (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() =>
                                                                requeueMutation.mutate(
                                                                    item.verificationId
                                                                )
                                                            }
                                                            disabled={
                                                                requeueingId ===
                                                                item.verificationId
                                                            }
                                                        >
                                                            {requeueingId === item.verificationId
                                                                ? "Requeueing..."
                                                                : "Requeue"}
                                                        </Button>
                                                    ) : null}
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() =>
                                                            navigate(
                                                                `/approvals/driver-licences/${item.verificationId}`
                                                            )
                                                        }
                                                    >
                                                        Review
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="flex flex-col gap-4 border-t border-slate-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-between dark:border-slate-800">
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        Showing {items.length} latest driver records
                        {hiddenAttemptCount > 0 ? `, ${hiddenAttemptCount} older attempts hidden` : ""}
                    </p>
                    <div className="flex items-center gap-3">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setFilter("page", Math.max(filters.page - 1, 0))}
                            disabled={!queue?.hasPrevious}
                        >
                            <ChevronLeftIcon className="mr-1 h-4 w-4" />
                            Previous
                        </Button>
                        <span className="text-sm text-slate-600 dark:text-slate-300">
                            Page {(queue?.page ?? 0) + 1} of {queue?.totalPages ?? 1}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setFilter("page", (queue?.page ?? 0) + 1)}
                            disabled={!queue?.hasNext}
                        >
                            Next
                            <ChevronRightIcon className="ml-1 h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
};
